import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Clock, Award, Users } from 'lucide-react';

export const About = () => {
  const stats = [
    { value: '20+', label: 'Years Experience' },
    { value: '5000+', label: 'Projects Completed' },
    { value: '100%', label: 'Satisfaction Rate' },
    { value: '24/7', label: 'Emergency Service' },
  ];

  const features = [
    {
      icon: Shield,
      title: 'Licensed & Insured',
      description: 'Fully licensed contractors with comprehensive insurance coverage for your protection and peace of mind.',
    },
    {
      icon: Clock,
      title: 'On-Time Delivery',
      description: 'We understand deadlines matter. Our team works efficiently to complete projects on schedule, every time.',
    },
    {
      icon: Award,
      title: 'Quality Guaranteed',
      description: 'We stand behind our work with a satisfaction guarantee. Precision cuts and professional results.',
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Skilled operators with extensive training in the latest concrete cutting techniques and safety protocols.',
    },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-20 md:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block font-heading text-sm font-semibold tracking-widest uppercase text-orange-500 mb-4">
              About Us
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-6">
              Florida's Premier
              <span className="block text-orange-500">Concrete Specialists</span>
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              For over two decades, Florida Concrete Cutting Services has been the trusted 
              choice for contractors, builders, and property owners throughout South West Florida. 
              We combine industry expertise with cutting-edge equipment to deliver 
              exceptional results.
            </p>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Our commitment to safety, precision, and customer satisfaction has made us 
              a leader in concrete cutting, core drilling, and demolition services. 
              No project is too big or too small for our experienced team.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center sm:text-left">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-orange-500">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-600 font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white p-6 border-l-4 border-orange-500 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_30px_-4px_rgba(249,115,22,0.15)] transition-shadow duration-300"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-100 text-orange-500 mb-4">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
